/**
 * CheckInPage.tsx
 *
 * Public patient self-check-in kiosk. Not behind auth.
 * Patients walk up to a tablet/screen and check themselves in.
 *
 * Flow:
 *  Step 1 — Search by name / date of birth
 *  Step 2 — Select appointment from matching results
 *  Step 3 — Confirm check-in
 *  Step 4 — Success (auto-resets to Step 1 after 10 s)
 */
import { useState, useEffect, useRef } from 'react';
import {
  getCalendarAppointments,
  checkInAppointment,
  type CalendarAppointment,
} from '@/services/appointments.service';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString([], {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
}

/** Simple name-match: checks patientName, title, and appointmentTypeName */
function matchesQuery(appt: CalendarAppointment, query: string): boolean {
  const q = query.toLowerCase().trim();
  if (!q) return false;
  const haystack = [appt.patientName, appt.title, appt.appointmentTypeName]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return q.split(/\s+/).every((token) => haystack.includes(token));
}

// ─── CheckInPage ──────────────────────────────────────────────────────────────

type Step = 1 | 2 | 3 | 4;

export default function CheckInPage() {
  const [step, setStep] = useState<Step>(1);
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [results, setResults] = useState<CalendarAppointment[]>([]);
  const [selected, setSelected] = useState<CalendarAppointment | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(10);

  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-focus the search input on step 1
  useEffect(() => {
    if (step === 1) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [step]);

  // Countdown timer on success screen, then reset
  useEffect(() => {
    if (step !== 4) return;

    setCountdown(10);
    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          handleReset();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  function handleReset() {
    setStep(1);
    setQuery('');
    setSearching(false);
    setSearchError(null);
    setResults([]);
    setSelected(null);
    setConfirming(false);
    setConfirmError(null);
  }

  // ── Step 1: Search ──────────────────────────────────────────────────────────

  async function handleSearch() {
    const trimmed = query.trim();
    if (!trimmed) return;

    setSearching(true);
    setSearchError(null);
    setResults([]);

    try {
      const today = todayISO();
      const all = await getCalendarAppointments(today, today);
      const matched = all.filter((a) => matchesQuery(a, trimmed));
      setResults(matched);
      setStep(2);
    } catch {
      setSearchError('Could not load appointments. Please ask a staff member for assistance.');
    } finally {
      setSearching(false);
    }
  }

  // ── Step 3: Confirm ─────────────────────────────────────────────────────────

  async function handleConfirm() {
    if (!selected) return;
    setConfirming(true);
    setConfirmError(null);
    try {
      await checkInAppointment(selected.id);
      setStep(4);
    } catch {
      setConfirmError('Check-in failed. Please ask a staff member for assistance.');
    } finally {
      setConfirming(false);
    }
  }

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-slate-100 flex flex-col items-center justify-center px-4 py-12">
      {/* Branding */}
      <div className="text-center mb-10">
        <h1 className="text-4xl font-extrabold text-indigo-700 tracking-tight">
          Prospera AI
        </h1>
        <p className="mt-2 text-xl text-slate-500 font-medium">Patient Check-In</p>
      </div>

      {/* Card */}
      <div className="w-full max-w-2xl mx-auto bg-white border border-slate-200 rounded-2xl shadow-xl p-8">

        {/* ── Step 1: Identity ─────────────────────────────────────────────── */}
        {step === 1 && (
          <div className="space-y-6">
            <h2 className="text-2xl font-semibold text-slate-900 text-center">
              Welcome! Let's find your appointment.
            </h2>

            {searchError && (
              <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 text-center">
                {searchError}
              </div>
            )}

            {/*
              `!text-lg`, not `text-lg`: the iOS auto-zoom floor in index.css raises
              every sub-16px control to 16px below 1024px on a touch device, and a
              plain `text-lg` (0,1,0) would lose to it and *shrink* this kiosk input
              from 18px. The important modifier emits the same 1.125rem/1.75rem it
              always did, so the computed values — desktop and print included — are
              unchanged; it only outranks the floor.
            */}
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') void handleSearch(); }}
              placeholder="Enter your name or date of birth"
              className="border border-slate-300 rounded-xl px-5 py-4 !text-lg w-full focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
            />

            <button
              onClick={() => void handleSearch()}
              disabled={searching || !query.trim()}
              className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white py-4 px-8 text-lg font-semibold rounded-xl transition-colors"
            >
              {searching ? 'Searching…' : 'Find My Appointment'}
            </button>
          </div>
        )}

        {/* ── Step 2: Select Appointment ───────────────────────────────────── */}
        {step === 2 && (
          <div className="space-y-5">
            <h2 className="text-2xl font-semibold text-slate-900 text-center">
              Select Your Appointment
            </h2>

            {results.length === 0 ? (
              <div className="text-center py-8 space-y-4">
                <p className="text-slate-500 text-lg">
                  No appointments found for "{query}" today.
                </p>
                <button
                  onClick={handleReset}
                  className="border border-slate-300 hover:bg-slate-50 px-8 py-4 rounded-xl text-lg font-medium text-slate-700"
                >
                  Try Again
                </button>
              </div>
            ) : (
              <>
                <div className="space-y-3">
                  {results.map((appt) => (
                    <button
                      key={appt.id}
                      onClick={() => { setSelected(appt); setStep(3); }}
                      className="w-full text-left bg-white border-2 border-slate-200 hover:border-indigo-400 hover:bg-indigo-50 rounded-xl py-4 px-6 transition-colors group"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xl font-semibold text-slate-900 group-hover:text-indigo-700">
                            {formatTime(appt.start)}
                          </p>
                          <p className="text-base text-slate-600 mt-0.5">
                            {appt.appointmentTypeName ?? appt.title ?? 'Appointment'}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-slate-500">{formatDate(appt.start)}</p>
                          {appt.patientName && (
                            <p className="text-sm font-medium text-slate-700 mt-0.5">
                              {appt.patientName}
                            </p>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>

                <button
                  onClick={handleReset}
                  className="w-full border border-slate-300 hover:bg-slate-50 px-8 py-3 rounded-xl text-base text-slate-600"
                >
                  Search Again
                </button>
              </>
            )}
          </div>
        )}

        {/* ── Step 3: Confirm ──────────────────────────────────────────────── */}
        {step === 3 && selected && (
          <div className="space-y-6">
            <h2 className="text-2xl font-semibold text-slate-900 text-center">
              Confirm Your Check-In
            </h2>

            <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-6 space-y-2">
              <p className="text-lg font-semibold text-indigo-900">
                {selected.appointmentTypeName ?? selected.title ?? 'Appointment'}
              </p>
              <p className="text-2xl font-bold text-indigo-700">
                {formatTime(selected.start)} – {formatTime(selected.end)}
              </p>
              <p className="text-slate-600">{formatDate(selected.start)}</p>
              {selected.patientName && (
                <p className="text-base font-medium text-slate-800 mt-1">
                  {selected.patientName}
                </p>
              )}
            </div>

            {confirmError && (
              <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 text-center">
                {confirmError}
              </div>
            )}

            <div className="flex gap-4">
              <button
                onClick={() => { setSelected(null); setStep(2); }}
                className="flex-1 border border-slate-300 hover:bg-slate-50 px-8 py-4 rounded-xl text-lg font-medium text-slate-700"
              >
                Back
              </button>
              <button
                onClick={() => void handleConfirm()}
                disabled={confirming}
                className="flex-1 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white py-4 px-8 text-lg font-semibold rounded-xl transition-colors"
              >
                {confirming ? 'Checking In…' : 'Confirm Check-In'}
              </button>
            </div>
          </div>
        )}

        {/* ── Step 4: Success ───────────────────────────────────────────────── */}
        {step === 4 && selected && (
          <div className="text-center space-y-6 py-4">
            {/* Checkmark */}
            <div className="flex justify-center">
              <div className="w-24 h-24 rounded-full bg-emerald-100 flex items-center justify-center">
                <svg
                  className="w-14 h-14 text-emerald-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2.5}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>

            <div>
              <h2 className="text-3xl font-bold text-emerald-700">You're Checked In!</h2>
              <p className="text-xl text-slate-600 mt-2">Please take a seat.</p>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 text-left space-y-1">
              <p className="text-lg font-semibold text-slate-900">
                {selected.appointmentTypeName ?? selected.title ?? 'Appointment'}
              </p>
              <p className="text-2xl font-bold text-indigo-700">
                {formatTime(selected.start)}
              </p>
              <p className="text-slate-500">{formatDate(selected.start)}</p>
            </div>

            <p className="text-slate-400 text-base">
              Returning to start in{' '}
              <span className="font-semibold text-slate-600">{countdown}</span>{' '}
              second{countdown !== 1 ? 's' : ''}…
            </p>

            <button
              onClick={handleReset}
              className="w-full border border-slate-300 hover:bg-slate-50 px-8 py-4 rounded-xl text-lg font-medium text-slate-700"
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
